/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  generatePatternStyles,
  getLinearGradientPoints,
} from '@web-stories-wp/patterns';
import { themeHelpers } from '@web-stories-wp/design-system';
import { __ } from '@web-stories-wp/i18n';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { OverlayType } from '../../utils/overlay';
import { getVisibleOverlayStack } from '../../utils/overlayStack';
import OverlayContext from '../overlay/context';
import { useTransformHandler } from '../transform';
import { upgradeRadialGeometry } from '../colorPicker/updateGradientGeometry';

const LINE_COLOR = '#919899';
const ENDPOINT_COLOR = '#e4e5e6';
const HANDLE_BACKGROUND = '#26292a';
const PICKER_STROKE_COLOR = '#919899';
const ENDPOINT_SIZE = 8;
const HANDLE_SIZE = 12;
const LINE_CENTER_OFFSET = 0.35;
const MIN_LINE_CENTER_OFFSET = 18;
const LINE_STOP_OFFSET = 15.5;
const LINE_STOP_HEIGHT = 23;
const LINE_STOP_WIDTH = 20;
const MARKER_EDGE_CLEARANCE = 12;
const RADIAL_STOP_OFFSET = 16;
const MIN_RADIAL_SIZE = 0.01;
const VECTOR_EPSILON = 0.0001;
const ANGLE_SNAP_STEP = Math.PI / 4;
const INDICATOR_Z_INDEX = 12;

const IndicatorLayer = styled.div.attrs(({ $frame, $isInOverlay }) => ({
  style: $isInOverlay
    ? {
        height: `${$frame.height}px`,
        left: `${$frame.x}px`,
        top: `${$frame.y}px`,
        transform: $frame.transform,
        width: `${$frame.width}px`,
      }
    : {
        inset: 0,
      },
}))`
  overflow: visible;
  pointer-events: none;
  position: absolute;
  transform-origin: center;
  z-index: ${INDICATOR_Z_INDEX};
`;

const LinearLine = styled.div.attrs(({ $geometry }) => ({
  style: {
    left: `${$geometry.left}px`,
    top: `${$geometry.top}px`,
    transform: `translate(-50%, -50%) rotate(${$geometry.angle}deg)`,
    width: `${$geometry.length}px`,
  },
}))`
  height: 1px;
  position: absolute;
  transform-origin: center;
  z-index: 0;
`;

const LinearVisibleLine = styled.div.attrs(({ $end, $start }) => ({
  style: {
    left: `${Math.min($start, $end) * 100}%`,
    width: `${Math.abs($end - $start) * 100}%`,
  },
}))`
  background: ${LINE_COLOR};
  height: 1px;
  position: absolute;
  top: 0;
  z-index: 0;
`;

const LinearProjectionPoint = styled.div.attrs(({ $position }) => ({
  'data-gradient-line-endpoint': '',
  style: {
    left: `${$position * 100}%`,
  },
}))`
  height: 0;
  position: absolute;
  top: 50%;
  width: 0;
`;

const LinearEndpoint = styled.div.attrs(({ $position }) => ({
  'data-gradient-endpoint': '',
  style: {
    left: `${$position * 100}%`,
  },
}))`
  background: ${ENDPOINT_COLOR};
  border-radius: 50%;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'grab' : 'default')};
  height: ${ENDPOINT_SIZE}px;
  pointer-events: auto;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  touch-action: none;
  width: ${ENDPOINT_SIZE}px;
  z-index: 1;

  &:active {
    cursor: ${({ $isInteractive }) =>
      $isInteractive ? 'grabbing' : 'default'};
  }
`;

const LinearStop = styled.div.attrs(({ $position }) => ({
  style: {
    left: `${$position * 100}%`,
  },
}))`
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'grab' : 'default')};
  height: ${LINE_STOP_HEIGHT}px;
  pointer-events: ${({ $isInteractive }) => ($isInteractive ? 'auto' : 'none')};
  position: absolute;
  top: calc(50% - ${LINE_STOP_OFFSET}px);
  transform: translate(-50%, -50%);
  touch-action: none;
  width: ${LINE_STOP_WIDTH}px;
  z-index: 2;

  &:active {
    cursor: ${({ $isInteractive }) =>
      $isInteractive ? 'grabbing' : 'default'};
  }
`;

const LinearStopSwatch = styled.div`
  ${themeHelpers.transparentBg}
  border-radius: 1px;
  inset: 13.04% 15% 26.09%;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
`;

const LinearStopSwatchColor = styled.div.attrs(({ $color }) => ({
  style: generatePatternStyles({ color: $color }),
}))`
  inset: 0;
  position: absolute;
`;

const LinearStopShape = styled.svg`
  display: block;
  height: 100%;
  overflow: visible;
  width: 100%;
`;

const RadialRail = styled.div.attrs(({ $geometry }) => ({
  style: {
    left: `${$geometry.left}px`,
    top: `${$geometry.top}px`,
    transform: `translate(-50%, -50%) rotate(${$geometry.angle}deg)`,
    width: `${$geometry.radiusY}px`,
  },
}))`
  background: ${LINE_COLOR};
  height: 1px;
  position: absolute;
  transform-origin: center;
  z-index: 0;
`;

const RadialProjectionPoint = styled.div.attrs(({ $position }) => ({
  'data-gradient-line-endpoint': '',
  style: {
    left: `${$position * 100}%`,
  },
}))`
  height: 0;
  position: absolute;
  top: 50%;
  width: 0;
`;

const RadialPoint = styled.div.attrs(({ $point }) => ({
  style: {
    left: `${$point.x}px`,
    top: `${$point.y}px`,
  },
}))`
  background: ${ENDPOINT_COLOR};
  border-radius: 50%;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'grab' : 'default')};
  height: ${ENDPOINT_SIZE}px;
  pointer-events: auto;
  position: absolute;
  transform: translate(-50%, -50%);
  touch-action: none;
  width: ${ENDPOINT_SIZE}px;
  z-index: 1;

  &:active {
    cursor: ${({ $isInteractive }) =>
      $isInteractive ? 'grabbing' : 'default'};
  }
`;

const RadialStop = styled.div.attrs(({ $geometry, $position }) => ({
  style: {
    left: `${
      $geometry.centerX +
      $geometry.axisX * $geometry.radiusY * $position -
      $geometry.perpendicularX * RADIAL_STOP_OFFSET
    }px`,
    top: `${
      $geometry.centerY +
      $geometry.axisY * $geometry.radiusY * $position -
      $geometry.perpendicularY * RADIAL_STOP_OFFSET
    }px`,
    transform: `translate(-50%, -50%) rotate(${$geometry.angle}deg)`,
  },
}))`
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'grab' : 'default')};
  height: ${LINE_STOP_HEIGHT}px;
  pointer-events: ${({ $isInteractive }) => ($isInteractive ? 'auto' : 'none')};
  position: absolute;
  touch-action: none;
  width: ${LINE_STOP_WIDTH}px;
  z-index: 2;

  &:active {
    cursor: ${({ $isInteractive }) =>
      $isInteractive ? 'grabbing' : 'default'};
  }
`;

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function clampRadialSize(value, minimum = MIN_RADIAL_SIZE) {
  return Math.max(minimum, value);
}

function getRadialSize(pattern) {
  const minimum = pattern.geometryVersion === 2 ? 0.0001 : MIN_RADIAL_SIZE;
  return {
    h: clampRadialSize(pattern.size?.h ?? 1, minimum),
    w: clampRadialSize(pattern.size?.w ?? 1, minimum),
  };
}

function getRoundedUnit(value) {
  return Number(clamp(value).toFixed(2));
}

function getRoundedPosition(value) {
  return Number(value.toFixed(4));
}

function sortStopsByPosition(stops = []) {
  return stops.slice().sort((left, right) => {
    return clamp(left.position) - clamp(right.position);
  });
}

function normalizeTurn(value) {
  return ((value % 1) + 1) % 1;
}

function getRoundedTurn(value) {
  const roundedValue = Number(normalizeTurn(value).toFixed(4));
  return roundedValue === 1 ? 0 : roundedValue;
}

function getColorKey(color = {}) {
  return color.id || `${color.r}-${color.g}-${color.b}-${color.a}`;
}

function getStopKeys(stops = []) {
  // These keys follow the dragged paint through sorting. Stateless DOM slots
  // use their index instead so changing RGB does not remount every handle.
  const occurrences = {};
  return stops.map(({ color }) => {
    const colorKey = getColorKey(color);
    const occurrence = occurrences[colorKey] || 0;
    occurrences[colorKey] = occurrence + 1;
    return `${colorKey}-${occurrence}`;
  });
}

function getInverseRotatedVector({ x, y }, rotationAngle = 0) {
  const angle = -((rotationAngle || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

function getLocalPointerDelta(startPointer, event, rotationAngle = 0) {
  return getInverseRotatedVector(
    {
      x: event.clientX - startPointer.x,
      y: event.clientY - startPointer.y,
    },
    rotationAngle
  );
}

function getLinearRotationFromPoints(start, end, { height, width }) {
  const dx = (end.x - start.x) * width;
  const dy = (end.y - start.y) * height;

  if (!dx && !dy) {
    return null;
  }

  return getRoundedTurn(Math.atan2(dy, dx) / (Math.PI * 2) - 0.25);
}

function snapLinearEndpoint(position, anchor, { height, width }) {
  // Snap in physical paint pixels so diagonals stay at 45° in non-square boxes.
  const dx = (position.x - anchor.x) * width;
  const dy = (position.y - anchor.y) * height;
  const length = Math.hypot(dx, dy);
  const angle =
    Math.round(Math.atan2(dy, dx) / ANGLE_SNAP_STEP) * ANGLE_SNAP_STEP;

  return {
    x: anchor.x + (Math.cos(angle) * length) / width,
    y: anchor.y + (Math.sin(angle) * length) / height,
  };
}

function getRectCenter(element) {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function getDistance(start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function stripTransformTranslation(transform) {
  if (!transform || transform === 'none') {
    return null;
  }

  const matrixMatch = transform.match(/^matrix\(([^)]+)\)$/);
  if (matrixMatch) {
    const values = matrixMatch[1].split(',').map((value) => value.trim());
    if (values.length === 6) {
      return `matrix(${values.slice(0, 4).join(', ')}, 0, 0)`;
    }
  }

  const matrix3dMatch = transform.match(/^matrix3d\(([^)]+)\)$/);
  if (matrix3dMatch) {
    const values = matrix3dMatch[1].split(',').map((value) => value.trim());
    if (values.length === 16) {
      values[12] = '0';
      values[13] = '0';
      values[14] = '0';
      return `matrix3d(${values.join(', ')})`;
    }
  }

  return transform;
}

function getOverlayFrame({ box, overlay, targetEl }) {
  const fallbackFrame = {
    height: box.height,
    rotationAngle: box.rotationAngle || 0,
    transform: `rotate(${box.rotationAngle || 0}deg)`,
    width: box.width,
    x: box.x || 0,
    y: box.y || 0,
  };

  if (!overlay || !targetEl) {
    return fallbackFrame;
  }

  const targetRect = targetEl.getBoundingClientRect();

  if (!targetRect.width && !targetRect.height) {
    return fallbackFrame;
  }

  const overlayRect = overlay.getBoundingClientRect();
  const targetStyle = window.getComputedStyle(targetEl);
  const height = targetEl.offsetHeight || box.height;
  const width = targetEl.offsetWidth || box.width;

  return {
    height,
    rotationAngle: box.rotationAngle || 0,
    transform:
      targetStyle.transform && targetStyle.transform !== 'none'
        ? stripTransformTranslation(targetStyle.transform)
        : fallbackFrame.transform,
    width,
    x: targetRect.left - overlayRect.left + (targetRect.width - width) / 2,
    y: targetRect.top - overlayRect.top + (targetRect.height - height) / 2,
  };
}

function getTargetBox({ box, targetEl }) {
  return {
    ...box,
    height: targetEl?.offsetHeight || box.height,
    width: targetEl?.offsetWidth || box.width,
  };
}

function getStopPositionFromPointer(lineEl, event) {
  const [startEl, endEl] = lineEl.querySelectorAll(
    '[data-gradient-line-endpoint]'
  );

  if (!startEl || !endEl) {
    return null;
  }

  const start = getRectCenter(startEl);
  const end = getRectCenter(endEl);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;

  if (!lengthSquared) {
    return null;
  }

  return clamp(
    ((event.clientX - start.x) * dx + (event.clientY - start.y) * dy) /
      lengthSquared
  );
}

function getLocalPointerPosition(containerEl, event, rotationAngle = 0) {
  const rect = containerEl.getBoundingClientRect();
  const height = containerEl.offsetHeight || rect.height;
  const width = containerEl.offsetWidth || rect.width;

  if (!width || !height) {
    return null;
  }

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const localDelta = getInverseRotatedVector(
    {
      x: event.clientX - centerX,
      y: event.clientY - centerY,
    },
    rotationAngle
  );

  return {
    x: getRoundedPosition((localDelta.x + width / 2) / width),
    y: getRoundedPosition((localDelta.y + height / 2) / height),
  };
}

function getEndpointPositions(stops) {
  if (!stops.length) {
    return [0, 1];
  }

  if (stops.length === 1) {
    const position = clamp(stops[0].position);
    return [position, position];
  }

  return [clamp(stops[0].position), clamp(stops[stops.length - 1].position)];
}

function getLinearPointAtPosition(geometry, position, { height, width }) {
  const clampedPosition = clamp(position);
  const round = geometry.isUnrestricted ? getRoundedPosition : getRoundedUnit;
  return {
    x: round(
      (geometry.start.x +
        (geometry.end.x - geometry.start.x) * clampedPosition) /
        width
    ),
    y: round(
      (geometry.start.y +
        (geometry.end.y - geometry.start.y) * clampedPosition) /
        height
    ),
  };
}

function getNormalizedLinearEndpointStops(pattern) {
  if (!Array.isArray(pattern.stops)) {
    return undefined;
  }

  if (pattern.start || pattern.end) {
    return pattern.stops;
  }

  const stops = pattern.stops;
  const [startPosition, endPosition] = getEndpointPositions(stops);
  const range = endPosition - startPosition;

  if (Math.abs(range) < VECTOR_EPSILON) {
    return stops;
  }

  return sortStopsByPosition(
    stops.map((stop) => ({
      ...stop,
      position: clamp((clamp(stop.position) - startPosition) / range),
    }))
  );
}

function pinLinearEndpointsForStopMove(pattern, box) {
  if (
    pattern?.type !== OverlayType.LINEAR ||
    !Array.isArray(pattern.stops) ||
    (pattern.start && pattern.end)
  ) {
    return {
      getPosition: (position) => position,
      pattern,
    };
  }

  const [startPosition, endPosition] = getEndpointPositions(pattern.stops);
  const range = endPosition - startPosition;
  const geometry = getLinearGeometry(box, pattern);
  const normalizedStops = getNormalizedLinearEndpointStops(pattern);

  return {
    getPosition: (position) =>
      Math.abs(range) < VECTOR_EPSILON
        ? clamp(position)
        : clamp((position - startPosition) / range),
    pattern: {
      ...pattern,
      end: getLinearPointAtPosition(geometry, endPosition, box),
      start: getLinearPointAtPosition(geometry, startPosition, box),
      ...(normalizedStops ? { stops: normalizedStops } : {}),
    },
  };
}

function getRayLength({ centerX, centerY, dx, dy, minX, maxX, minY, maxY }) {
  const lengths = [];

  if (dx > VECTOR_EPSILON) {
    lengths.push((maxX - centerX) / dx);
  } else if (dx < -VECTOR_EPSILON) {
    lengths.push((minX - centerX) / dx);
  }

  if (dy > VECTOR_EPSILON) {
    lengths.push((maxY - centerY) / dy);
  } else if (dy < -VECTOR_EPSILON) {
    lengths.push((minY - centerY) / dy);
  }

  if (!lengths.length) {
    return 0;
  }

  return Math.max(0, Math.min(...lengths));
}

function isGradientPattern(pattern) {
  return (
    pattern?.type === OverlayType.LINEAR || pattern?.type === OverlayType.RADIAL
  );
}

function isGradientFilter(filter) {
  return isGradientPattern(filter?.pattern);
}

function getDisplayFilter(element, activeFilterId) {
  if (!activeFilterId) {
    return null;
  }

  const visibleFilters = getVisibleOverlayStack(element);
  const activeFilter = visibleFilters.find(({ id }) => id === activeFilterId);

  return isGradientFilter(activeFilter) ? activeFilter : null;
}

function useLiveTransformRemeasure(elementId, shouldRemeasure, onTransform) {
  const [, setTransformRevision] = useState(0);
  const handleTransform = useCallback(
    (transform) => {
      onTransform?.(transform);

      // Static colour/filter previews do not move the element's frame. Their
      // paint handler already updates the displayed pattern when needed.
      if (!shouldRemeasure || transform?.staticTransformation) {
        return;
      }

      setTransformRevision((revision) => revision + 1);
    },
    [onTransform, shouldRemeasure]
  );

  useTransformHandler(elementId, handleTransform, [elementId, handleTransform]);
}

function getLinearGeometry({ height, width }, pattern) {
  if (pattern.geometryVersion === 2 && !(pattern.start && pattern.end)) {
    const points = getLinearGradientPoints(pattern, { width, height });
    pattern = {
      ...pattern,
      start: { x: points.start.x / width, y: points.start.y / height },
      end: { x: points.end.x / width, y: points.end.y / height },
    };
  }
  if (pattern.start && pattern.end) {
    const startX = width * pattern.start.x;
    const startY = height * pattern.start.y;
    const endX = width * pattern.end.x;
    const endY = height * pattern.end.y;
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length > VECTOR_EPSILON) {
      return {
        isUnrestricted: pattern.geometryVersion === 2,
        angle: (Math.atan2(dy, dx) * 180) / Math.PI,
        end: { x: endX, y: endY },
        left: (startX + endX) / 2,
        length,
        start: { x: startX, y: startY },
        top: (startY + endY) / 2,
      };
    }
  }

  const { rotation = 0 } = pattern;
  const angle = ((rotation || 0) + 0.25) * 360;
  const angleRadians = (angle * Math.PI) / 180;
  const rawDx = Math.cos(angleRadians);
  const rawDy = Math.sin(angleRadians);
  const dx = Math.abs(rawDx) < VECTOR_EPSILON ? 0 : rawDx;
  const dy = Math.abs(rawDy) < VECTOR_EPSILON ? 0 : rawDy;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const minX = 0;
  const maxX = width;
  const minY = 0;
  const maxY = height;
  let normalX = -dy;
  let normalY = dx;

  if (Math.abs(normalX) < VECTOR_EPSILON) {
    normalY = normalY < 0 ? normalY : -normalY;
  } else if (normalX < 0) {
    normalX *= -1;
    normalY *= -1;
  }

  const halfNormal =
    Math.abs(normalX) * halfWidth + Math.abs(normalY) * halfHeight;
  const offset = Math.min(
    Math.max(MIN_LINE_CENTER_OFFSET, halfNormal * LINE_CENTER_OFFSET),
    Math.max(0, halfNormal - HANDLE_SIZE)
  );
  const markerClearance =
    LINE_STOP_OFFSET + LINE_STOP_HEIGHT / 2 + MARKER_EDGE_CLEARANCE;
  const clearanceX = Math.min(markerClearance, halfWidth);
  const clearanceY = Math.min(markerClearance, halfHeight);
  const centerX = clamp(
    halfWidth + normalX * offset,
    normalX < 0 ? clearanceX : minX,
    normalX > 0 ? maxX - clearanceX : maxX
  );
  const centerY = clamp(
    halfHeight + normalY * offset,
    normalY < 0 ? clearanceY : minY,
    normalY > 0 ? maxY - clearanceY : maxY
  );
  const forwardLength = getRayLength({
    centerX,
    centerY,
    dx,
    dy,
    minX,
    maxX,
    minY,
    maxY,
  });
  const backwardLength = getRayLength({
    centerX,
    centerY,
    dx: -dx,
    dy: -dy,
    minX,
    maxX,
    minY,
    maxY,
  });
  const startX = centerX - dx * backwardLength;
  const startY = centerY - dy * backwardLength;
  const endX = centerX + dx * forwardLength;
  const endY = centerY + dy * forwardLength;

  return {
    angle,
    end: { x: endX, y: endY },
    left: (startX + endX) / 2,
    length: forwardLength + backwardLength,
    start: { x: startX, y: startY },
    top: (startY + endY) / 2,
  };
}

function getAxisFromRotation(rotation = 0) {
  const angle = ((rotation || 0) + 0.25) * Math.PI * 2;
  return {
    angle: (angle * 180) / Math.PI,
    x: Math.abs(Math.cos(angle)) < VECTOR_EPSILON ? 0 : Math.cos(angle),
    y: Math.abs(Math.sin(angle)) < VECTOR_EPSILON ? 0 : Math.sin(angle),
  };
}

function getRadialGeometry({ height, width }, pattern) {
  const centerX = width * (pattern.center?.x ?? 0.5);
  const centerY = height * (pattern.center?.y ?? 0.5);
  const size = getRadialSize(pattern);
  const radiusX = (width * size.w) / 2;
  const radiusY = (height * size.h) / 2;
  const axis = getAxisFromRotation(pattern.rotation);
  const perpendicularX = -axis.y;
  const perpendicularY = axis.x;

  return {
    angle: axis.angle,
    axisX: axis.x,
    axisY: axis.y,
    centerX,
    centerY,
    left: centerX + (axis.x * radiusY) / 2,
    perpendicularX,
    perpendicularY,
    radiusX,
    radiusY,
    top: centerY + (axis.y * radiusY) / 2,
    radiusXPoint: {
      x: centerX + perpendicularX * radiusX,
      y: centerY + perpendicularY * radiusX,
    },
    radiusYPoint: {
      x: centerX + axis.x * radiusY,
      y: centerY + axis.y * radiusY,
    },
  };
}

function LinearIndicator({
  box,
  onCancelPattern,
  onCommitPattern,
  onMoveEndpoint,
  onMoveStop,
  onOpenPicker,
  pattern,
}) {
  const activeDrag = useRef(null);
  useEffect(() => {
    const cancelOnEscape = (event) => {
      if (event.key !== 'Escape' || !activeDrag.current) return;
      event.preventDefault();
      event.stopPropagation();
      activeDrag.current();
    };
    document.addEventListener('keydown', cancelOnEscape, true);
    return () => {
      document.removeEventListener('keydown', cancelOnEscape, true);
      activeDrag.current?.();
    };
  }, []);
  const lineRef = useRef(null);
  const geometry = useMemo(
    () => getLinearGeometry(box, pattern),
    [box, pattern]
  );
  const { stops = [] } = pattern;
  const stopKeys = useMemo(() => getStopKeys(stops), [stops]);
  const isInteractive = Boolean(onMoveStop);
  const hasEndpointControl = Boolean(onMoveEndpoint);
  const endpointPositions = useMemo(
    () => (pattern.start && pattern.end ? [0, 1] : getEndpointPositions(stops)),
    [pattern.end, pattern.start, stops]
  );

  const handleIndicatorEvent = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpenPicker?.();
    },
    [onOpenPicker]
  );
  const handlePrimaryIndicatorEvent = useCallback(
    (event) => {
      if (typeof event.button === 'number' && event.button !== 0) {
        return;
      }

      handleIndicatorEvent(event);
    },
    [handleIndicatorEvent]
  );

  const getPointerPosition = useCallback((event) => {
    if (!lineRef.current) {
      return null;
    }

    return getStopPositionFromPointer(lineRef.current, event);
  }, []);

  const handlePointerDown = useCallback(
    (index) => (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveStop) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      const startPointerPosition = getPointerPosition(event);

      if (startPointerPosition === null) {
        return;
      }

      const startStopPosition = clamp(stops[index]?.position ?? 0);
      const handlePointerMove = (pointerEvent) => {
        const pointerPosition = getPointerPosition(pointerEvent);

        if (pointerPosition === null) {
          return;
        }

        pointerEvent.preventDefault();
        onMoveStop(
          stopKeys[index],
          clamp(startStopPosition + pointerPosition - startPointerPosition)
        );
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      getPointerPosition,
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveStop,
      stopKeys,
      stops,
    ]
  );
  const handleEndpointPointerDown = useCallback(
    (endpointIndex) => (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveEndpoint) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      const indicatorEl = lineRef.current?.parentElement;

      if (!indicatorEl) {
        return;
      }

      const currentStart =
        pattern.start ||
        getLinearPointAtPosition(geometry, endpointPositions[0], box);
      const currentEnd =
        pattern.end ||
        getLinearPointAtPosition(geometry, endpointPositions[1], box);

      const handlePointerMove = (pointerEvent) => {
        const pointerPosition = getLocalPointerPosition(
          indicatorEl,
          pointerEvent,
          box.rotationAngle
        );

        if (!pointerPosition) {
          return;
        }

        const position = pointerEvent.shiftKey
          ? snapLinearEndpoint(
              pointerPosition,
              endpointIndex === 0 ? currentEnd : currentStart,
              box
            )
          : pointerPosition;
        const start = endpointIndex === 0 ? position : currentStart;
        const end = endpointIndex === 1 ? position : currentEnd;
        const rotation = getLinearRotationFromPoints(start, end, box);

        if (rotation === null) {
          return;
        }

        pointerEvent.preventDefault();
        onMoveEndpoint({ end, rotation, start });
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      box,
      geometry,
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveEndpoint,
      pattern.end,
      pattern.start,
      endpointPositions,
    ]
  );

  return (
    <LinearLine
      ref={lineRef}
      $geometry={geometry}
      data-testid="filter-gradient-indicator"
    >
      <LinearProjectionPoint $position={0} />
      <LinearProjectionPoint $position={1} />
      <LinearVisibleLine
        $start={endpointPositions[0]}
        $end={endpointPositions[1]}
        data-testid="filter-gradient-visible-line"
      />
      <LinearEndpoint
        $isInteractive={hasEndpointControl}
        $position={endpointPositions[0]}
        data-testid="filter-gradient-endpoint"
        title={
          hasEndpointControl
            ? __('Hold Shift to snap to 45° increments', 'web-stories')
            : undefined
        }
        onClick={handlePrimaryIndicatorEvent}
        onMouseDown={handlePrimaryIndicatorEvent}
        onPointerDown={handleEndpointPointerDown(0)}
      />
      <LinearEndpoint
        $isInteractive={hasEndpointControl}
        $position={endpointPositions[1]}
        data-testid="filter-gradient-endpoint"
        title={
          hasEndpointControl
            ? __('Hold Shift to snap to 45° increments', 'web-stories')
            : undefined
        }
        onClick={handlePrimaryIndicatorEvent}
        onMouseDown={handlePrimaryIndicatorEvent}
        onPointerDown={handleEndpointPointerDown(1)}
      />
      {stops.map(({ color, position }, index) => (
        <LinearStop
          key={index}
          $isInteractive={isInteractive}
          $position={clamp(position)}
          data-testid="filter-gradient-stop"
          onClick={handleIndicatorEvent}
          onMouseDown={handleIndicatorEvent}
          onPointerDown={handlePointerDown(index)}
        >
          <LinearStopShape viewBox="0 0 20 23.0002">
            <path
              d="M0.5 2.095C0.5 1.241 1.243 0.5 2.222 0.5H17.778C18.758 0.5 19.5 1.241 19.5 2.095V17.905C19.5 18.759 18.757 19.5 17.778 19.5H13.8C12.88 19.5 11.944 19.856 11.433 20.711L10.539 22.21C10.309 22.597 9.692 22.597 9.461 22.21L8.566 20.711C8.078 19.892 7.169 19.4 6.2 19.4H2.222C1.242 19.4 0.5 18.658 0.5 17.805V2.095Z"
              fill={HANDLE_BACKGROUND}
              stroke={PICKER_STROKE_COLOR}
            />
          </LinearStopShape>
          <LinearStopSwatch data-testid="filter-gradient-stop-swatch">
            <LinearStopSwatchColor
              $color={color}
              data-testid="filter-gradient-stop-swatch-color"
            />
          </LinearStopSwatch>
        </LinearStop>
      ))}
    </LinearLine>
  );
}

LinearIndicator.propTypes = {
  box: PropTypes.shape({
    height: PropTypes.number.isRequired,
    rotationAngle: PropTypes.number,
    width: PropTypes.number.isRequired,
  }).isRequired,
  onCancelPattern: PropTypes.func,
  onCommitPattern: PropTypes.func,
  onMoveEndpoint: PropTypes.func,
  onMoveStop: PropTypes.func,
  onOpenPicker: PropTypes.func,
  pattern: PropTypes.object.isRequired,
};

function RadialIndicator({
  box,
  onCancelPattern,
  onCommitPattern,
  onMoveAxis,
  onMoveCenter,
  onMoveRadius,
  onMoveStop,
  onOpenPicker,
  pattern,
}) {
  const railRef = useRef(null);
  const activeDrag = useRef(null);
  useEffect(() => {
    const cancelOnEscape = (event) => {
      if (event.key !== 'Escape' || !activeDrag.current) return;
      event.preventDefault();
      event.stopPropagation();
      activeDrag.current();
    };
    document.addEventListener('keydown', cancelOnEscape, true);
    return () => {
      document.removeEventListener('keydown', cancelOnEscape, true);
      activeDrag.current?.();
    };
  }, []);
  const centerRef = useRef(null);
  const radiusXRef = useRef(null);
  const radiusYRef = useRef(null);
  const geometry = useMemo(
    () => getRadialGeometry(box, pattern),
    [box, pattern]
  );
  const { stops = [] } = pattern;
  const stopKeys = useMemo(() => getStopKeys(stops), [stops]);
  const isInteractive = Boolean(onMoveStop);

  const handleIndicatorEvent = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpenPicker?.();
    },
    [onOpenPicker]
  );
  const handlePrimaryIndicatorEvent = useCallback(
    (event) => {
      if (typeof event.button === 'number' && event.button !== 0) {
        return;
      }

      handleIndicatorEvent(event);
    },
    [handleIndicatorEvent]
  );

  const getPointerPosition = useCallback((event) => {
    if (!railRef.current) {
      return null;
    }

    return getStopPositionFromPointer(railRef.current, event);
  }, []);

  const handlePointerDown = useCallback(
    (index) => (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveStop) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      const startPointerPosition = getPointerPosition(event);

      if (startPointerPosition === null) {
        return;
      }

      const startStopPosition = clamp(stops[index]?.position ?? 0);
      const handlePointerMove = (pointerEvent) => {
        const pointerPosition = getPointerPosition(pointerEvent);

        if (pointerPosition === null) {
          return;
        }

        pointerEvent.preventDefault();
        onMoveStop(
          stopKeys[index],
          clamp(startStopPosition + pointerPosition - startPointerPosition)
        );
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      getPointerPosition,
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveStop,
      stopKeys,
      stops,
    ]
  );
  const handleCenterPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveCenter) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      if (!box.width || !box.height) {
        return;
      }

      const startPointer = { x: event.clientX, y: event.clientY };
      const startCenter = {
        x: pattern.center?.x ?? 0.5,
        y: pattern.center?.y ?? 0.5,
      };
      const handlePointerMove = (pointerEvent) => {
        const delta = getLocalPointerDelta(
          startPointer,
          pointerEvent,
          box.rotationAngle
        );
        const center = {
          x: getRoundedPosition(startCenter.x + delta.x / box.width),
          y: getRoundedPosition(startCenter.y + delta.y / box.height),
        };

        pointerEvent.preventDefault();
        onMoveCenter(center);
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      box.height,
      box.rotationAngle,
      box.width,
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveCenter,
      pattern.center,
    ]
  );
  const handleAxisPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveAxis) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      if (!box.width || !box.height) {
        return;
      }

      const startPointer = { x: event.clientX, y: event.clientY };
      const center = {
        x: pattern.center?.x ?? 0.5,
        y: pattern.center?.y ?? 0.5,
      };
      const startEndpoint = {
        x: geometry.radiusYPoint.x / box.width,
        y: geometry.radiusYPoint.y / box.height,
      };
      const handlePointerMove = (pointerEvent) => {
        const delta = getLocalPointerDelta(
          startPointer,
          pointerEvent,
          box.rotationAngle
        );
        const endpoint = {
          x: getRoundedPosition(startEndpoint.x + delta.x / box.width),
          y: getRoundedPosition(startEndpoint.y + delta.y / box.height),
        };
        const rotation = getLinearRotationFromPoints(center, endpoint, {
          height: box.height,
          width: box.width,
        });

        if (rotation === null) {
          return;
        }

        const radiusY = getDistance(
          { x: center.x * box.width, y: center.y * box.height },
          { x: endpoint.x * box.width, y: endpoint.y * box.height }
        );

        pointerEvent.preventDefault();
        onMoveAxis({
          rotation,
          sizeH: Number(
            clampRadialSize((radiusY / box.height) * 2, 0.0001).toFixed(4)
          ),
        });
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      box.height,
      box.rotationAngle,
      box.width,
      geometry.radiusYPoint.x,
      geometry.radiusYPoint.y,
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveAxis,
      pattern.center,
    ]
  );
  const handleRadiusPointerDown = useCallback(
    (dimension, radiusRef) => (event) => {
      if (event.button !== 0) {
        return;
      }

      if (!onMoveRadius) {
        handleIndicatorEvent(event);
        return;
      }

      handleIndicatorEvent(event);

      if (!centerRef.current || !radiusRef.current) {
        return;
      }

      const center = getRectCenter(centerRef.current);
      const radius = getRectCenter(radiusRef.current);
      const dx = radius.x - center.x;
      const dy = radius.y - center.y;
      const startDistance = getDistance(center, radius);

      if (!startDistance) {
        return;
      }

      const startSize = getRadialSize(pattern)[dimension];
      const handlePointerMove = (pointerEvent) => {
        const projectedDistance =
          ((pointerEvent.clientX - center.x) * dx +
            (pointerEvent.clientY - center.y) * dy) /
          startDistance;
        const nextSize = Number(
          clampRadialSize(
            (Math.max(0, projectedDistance) / startDistance) * startSize,
            0.0001
          ).toFixed(4)
        );

        pointerEvent.preventDefault();
        onMoveRadius(dimension, nextSize);
      };
      const cleanup = () => {
        activeDrag.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
      const handlePointerUp = () => {
        cleanup();
        onCommitPattern?.();
      };
      const handlePointerCancel = () => {
        cleanup();
        onCancelPattern?.();
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      activeDrag.current = handlePointerCancel;
    },
    [
      handleIndicatorEvent,
      onCancelPattern,
      onCommitPattern,
      onMoveRadius,
      pattern.size,
    ]
  );

  return (
    <>
      <RadialRail
        ref={railRef}
        $geometry={geometry}
        data-testid="filter-gradient-indicator"
      >
        <RadialProjectionPoint $position={0} />
        <RadialProjectionPoint $position={1} />
      </RadialRail>
      <RadialPoint
        ref={centerRef}
        $isInteractive={Boolean(onMoveCenter)}
        $point={{ x: geometry.centerX, y: geometry.centerY }}
        data-testid="filter-gradient-center"
        onClick={handlePrimaryIndicatorEvent}
        onMouseDown={handlePrimaryIndicatorEvent}
        onPointerDown={handleCenterPointerDown}
      />
      <RadialPoint
        ref={radiusXRef}
        $isInteractive={Boolean(onMoveRadius)}
        $point={geometry.radiusXPoint}
        data-testid="filter-gradient-radius-x"
        onClick={handlePrimaryIndicatorEvent}
        onMouseDown={handlePrimaryIndicatorEvent}
        onPointerDown={handleRadiusPointerDown('w', radiusXRef)}
      />
      <RadialPoint
        ref={radiusYRef}
        $isInteractive={Boolean(onMoveAxis)}
        $point={geometry.radiusYPoint}
        data-testid="filter-gradient-radius-y"
        onClick={handlePrimaryIndicatorEvent}
        onMouseDown={handlePrimaryIndicatorEvent}
        onPointerDown={handleAxisPointerDown}
      />
      {stops.map(({ color, position }, index) => {
        const isStartHandle = index === 0 && clamp(position) === 0;
        const isEndHandle = index === stops.length - 1 && clamp(position) === 1;
        let pointerHandler = handlePointerDown(index);

        if (isStartHandle) {
          pointerHandler = handleCenterPointerDown;
        } else if (isEndHandle) {
          pointerHandler = handleAxisPointerDown;
        }

        return (
          <RadialStop
            key={index}
            $geometry={geometry}
            $isInteractive={isInteractive}
            $position={clamp(position)}
            data-testid="filter-gradient-stop"
            onClick={handleIndicatorEvent}
            onMouseDown={handleIndicatorEvent}
            onPointerDown={pointerHandler}
          >
            <LinearStopShape viewBox="0 0 20 23.0002">
              <path
                d="M0.5 2.095C0.5 1.241 1.243 0.5 2.222 0.5H17.778C18.758 0.5 19.5 1.241 19.5 2.095V17.905C19.5 18.759 18.757 19.5 17.778 19.5H13.8C12.88 19.5 11.944 19.856 11.433 20.711L10.539 22.21C10.309 22.597 9.692 22.597 9.461 22.21L8.566 20.711C8.078 19.892 7.169 19.4 6.2 19.4H2.222C1.242 19.4 0.5 18.658 0.5 17.805V2.095Z"
                fill={HANDLE_BACKGROUND}
                stroke={PICKER_STROKE_COLOR}
              />
            </LinearStopShape>
            <LinearStopSwatch data-testid="filter-gradient-stop-swatch">
              <LinearStopSwatchColor
                $color={color}
                data-testid="filter-gradient-stop-swatch-color"
              />
            </LinearStopSwatch>
          </RadialStop>
        );
      })}
    </>
  );
}

RadialIndicator.propTypes = {
  box: PropTypes.shape({
    height: PropTypes.number.isRequired,
    rotationAngle: PropTypes.number,
    width: PropTypes.number.isRequired,
  }).isRequired,
  onCancelPattern: PropTypes.func,
  onCommitPattern: PropTypes.func,
  onMoveAxis: PropTypes.func,
  onMoveCenter: PropTypes.func,
  onMoveRadius: PropTypes.func,
  onMoveStop: PropTypes.func,
  onOpenPicker: PropTypes.func,
  pattern: PropTypes.object.isRequired,
};

function FilterCanvasIndicator({
  activeFilterId,
  box,
  element,
  onOpenPicker,
  onResetPatternPreview,
  onUpdatePattern,
  targetEl,
  pattern: suppliedPattern,
  overlayTarget,
  paintFrame,
  pickerId,
  legacyPaint = suppliedPattern ? 'css' : 'filter',
}) {
  const { overlay } = useContext(OverlayContext);
  const filter = getDisplayFilter(element, activeFilterId);

  const pattern = suppliedPattern || filter?.pattern;
  const patternKey = JSON.stringify(pattern);
  const [previewPattern, setPreviewPattern] = useState(null);
  const previewPatternRef = useRef(null);
  const handlePatternTransformPreview = useCallback(
    (transform) => {
      if (transform === null) {
        previewPatternRef.current = null;
        setPreviewPattern(null);
        return;
      }

      const { color, overlayFilterId, resetOverlayFilterPreview, style } =
        transform;

      if (overlayFilterId !== activeFilterId || style !== 'background') {
        return;
      }

      if (resetOverlayFilterPreview) {
        previewPatternRef.current = null;
        setPreviewPattern(null);
        return;
      }

      if (color) {
        previewPatternRef.current = color;
        setPreviewPattern(color);
      }
    },
    [activeFilterId]
  );
  useLiveTransformRemeasure(
    element.id,
    Boolean(filter || suppliedPattern),
    handlePatternTransformPreview
  );
  const portalTarget = overlayTarget || overlay || targetEl;
  const isInOverlay = Boolean(overlay || overlayTarget);
  const frame =
    paintFrame ||
    getOverlayFrame({ box, overlay: overlayTarget || overlay, targetEl });
  const indicatorBox = isInOverlay ? frame : getTargetBox({ box, targetEl });
  const displayPattern = previewPattern || pattern;
  const displayPatternTypeRef = useRef(displayPattern?.type);
  displayPatternTypeRef.current = displayPattern?.type;
  const updatePatternPreview = useCallback(
    (getNextPattern) => {
      if (!displayPattern || !onUpdatePattern) {
        return;
      }

      const nextPattern = getNextPattern(
        previewPatternRef.current || displayPattern
      );
      previewPatternRef.current = nextPattern;
      setPreviewPattern(nextPattern);
      onUpdatePattern(() => nextPattern, { isPreview: true });
    },
    [displayPattern, onUpdatePattern]
  );
  const commitPatternPreview = useCallback(() => {
    if (!previewPatternRef.current || !onUpdatePattern) {
      return;
    }

    const nextPattern = previewPatternRef.current;
    previewPatternRef.current = null;
    setPreviewPattern(null);
    onUpdatePattern(() => nextPattern);
  }, [onUpdatePattern]);
  const cancelPatternPreview = useCallback(() => {
    if (!previewPatternRef.current) {
      return;
    }

    previewPatternRef.current = null;
    setPreviewPattern(null);
    onResetPatternPreview?.();
  }, [onResetPatternPreview]);
  useEffect(() => {
    previewPatternRef.current = null;
    setPreviewPattern(null);
  }, [activeFilterId, element.id, filter?.id, patternKey]);
  const handleMoveStop = useCallback(
    (stopKey, position) => {
      if (!updatePatternPreview) {
        return;
      }

      updatePatternPreview((currentPattern) => {
        // Only the legacy filter renderer consumes compensating endpoints.
        // CSS/SVG fills keep their absolute stop positions instead.
        const { getPosition, pattern: pinnedPattern } = suppliedPattern
          ? { getPosition: (value) => value, pattern: currentPattern }
          : pinLinearEndpointsForStopMove(currentPattern, indicatorBox);
        const nextPosition = getPosition(position);
        const pinnedStopKeys = getStopKeys(pinnedPattern.stops || []);

        return {
          ...pinnedPattern,
          stops: sortStopsByPosition(
            (pinnedPattern.stops || []).map((stop, index) =>
              pinnedStopKeys[index] === stopKey
                ? { ...stop, position: nextPosition }
                : stop
            )
          ),
        };
      });
    },
    [indicatorBox, suppliedPattern, updatePatternPreview]
  );
  const handleMoveLinearEndpoint = useCallback(
    ({ end, rotation, start }) => {
      if (
        !updatePatternPreview ||
        displayPatternTypeRef.current !== OverlayType.LINEAR
      ) {
        return;
      }

      updatePatternPreview((currentPattern) => {
        if (currentPattern.type !== OverlayType.LINEAR) {
          return currentPattern;
        }

        const stops = suppliedPattern
          ? currentPattern.stops
          : getNormalizedLinearEndpointStops(currentPattern);
        return {
          ...currentPattern,
          end,
          rotation,
          start,
          geometryVersion: 2,
          ...(stops ? { stops } : {}),
        };
      });
    },
    [suppliedPattern, updatePatternPreview]
  );
  const handleMoveRadialSize = useCallback(
    (dimension, size) => {
      if (
        !updatePatternPreview ||
        displayPatternTypeRef.current !== OverlayType.RADIAL
      ) {
        return;
      }

      updatePatternPreview((currentPattern) => ({
        ...upgradeRadialGeometry(currentPattern, legacyPaint),
        geometryVersion: 2,
        size: {
          ...getRadialSize(upgradeRadialGeometry(currentPattern, legacyPaint)),
          [dimension]: size,
        },
      }));
    },
    [legacyPaint, updatePatternPreview]
  );
  const handleMoveRadialAxis = useCallback(
    ({ rotation, sizeH }) => {
      if (
        !updatePatternPreview ||
        displayPatternTypeRef.current !== OverlayType.RADIAL
      ) {
        return;
      }

      updatePatternPreview((currentPattern) => ({
        ...upgradeRadialGeometry(currentPattern, legacyPaint),
        rotation,
        geometryVersion: 2,
        size: {
          ...getRadialSize(upgradeRadialGeometry(currentPattern, legacyPaint)),
          h: sizeH,
        },
      }));
    },
    [legacyPaint, updatePatternPreview]
  );
  const handleMoveRadialCenter = useCallback(
    (center) => {
      if (
        !updatePatternPreview ||
        displayPatternTypeRef.current !== OverlayType.RADIAL
      ) {
        return;
      }

      updatePatternPreview((currentPattern) => ({
        ...upgradeRadialGeometry(currentPattern, legacyPaint),
        center,
        geometryVersion: 2,
      }));
    },
    [legacyPaint, updatePatternPreview]
  );
  const handleOpenPicker = useCallback(() => {
    onOpenPicker?.(filter?.id);
  }, [filter?.id, onOpenPicker]);

  if (
    !portalTarget ||
    (!suppliedPattern && !isGradientFilter(filter)) ||
    !isGradientPattern(displayPattern)
  ) {
    return null;
  }

  return createPortal(
    <IndicatorLayer
      $frame={frame}
      $isInOverlay={isInOverlay}
      data-filter-gradient-indicator=""
      data-gradient-picker-id={pickerId}
      data-testid="filter-canvas-indicator"
    >
      {displayPattern.type === OverlayType.LINEAR ? (
        <LinearIndicator
          box={indicatorBox}
          onCancelPattern={cancelPatternPreview}
          onCommitPattern={commitPatternPreview}
          onMoveEndpoint={onUpdatePattern ? handleMoveLinearEndpoint : null}
          onMoveStop={onUpdatePattern ? handleMoveStop : null}
          onOpenPicker={handleOpenPicker}
          pattern={displayPattern}
        />
      ) : (
        <RadialIndicator
          box={indicatorBox}
          onCancelPattern={cancelPatternPreview}
          onCommitPattern={commitPatternPreview}
          onMoveAxis={onUpdatePattern ? handleMoveRadialAxis : null}
          onMoveCenter={onUpdatePattern ? handleMoveRadialCenter : null}
          onMoveRadius={onUpdatePattern ? handleMoveRadialSize : null}
          onMoveStop={onUpdatePattern ? handleMoveStop : null}
          onOpenPicker={handleOpenPicker}
          pattern={upgradeRadialGeometry(displayPattern, legacyPaint)}
        />
      )}
    </IndicatorLayer>,
    portalTarget
  );
}

FilterCanvasIndicator.propTypes = {
  legacyPaint: PropTypes.oneOf(['css', 'svg', 'filter']),
  activeFilterId: PropTypes.string,
  box: PropTypes.shape({
    height: PropTypes.number.isRequired,
    rotationAngle: PropTypes.number,
    width: PropTypes.number.isRequired,
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  element: PropTypes.object.isRequired,
  onOpenPicker: PropTypes.func,
  onResetPatternPreview: PropTypes.func,
  onUpdatePattern: PropTypes.func,
  targetEl: PropTypes.object,
  pattern: PropTypes.object,
  overlayTarget: PropTypes.object,
  paintFrame: PropTypes.object,
  pickerId: PropTypes.string,
};

export default FilterCanvasIndicator;
